import React, { useState, useEffect, useMemo, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

interface RentChartEmbedProps {
  initialRentValue?: number;
  initialCropName?: string;
}

// X-axis yield points
const yieldPoints = [0, 13.9, 27.8, 41.7, 55.6, 69.5, 83.4, 97.3, 111.2, 125.1, 139];

// Generate chart data based on rent value
const generateChartData = (baseRent: number) => {
  const scale = baseRent / 200;

  const landownerNoInsurance = yieldPoints.map((y) => {
    const base = 75 * scale;
    const variation = Math.sin(y / 30) * 15 * scale;
    return Math.round(base + variation);
  });

  const landownerWithInsurance = yieldPoints.map((y) => {
    const start = 50 * scale;
    const increase = (y / 139) * 120 * scale;
    return Math.round(start + increase);
  });

  const farmerNoInsurance = yieldPoints.map((y) => {
    const start = -220 * scale;
    const end = 340 * scale;
    const progress = y / 139;
    return Math.round(start + progress * (end - start));
  });

  const farmerWithInsurance = yieldPoints.map((y) => {
    const start = 0;
    const end = 340 * scale;
    const progress = y / 139;
    return Math.round(start + Math.pow(progress, 0.9) * end);
  });

  return { landownerNoInsurance, landownerWithInsurance, farmerNoInsurance, farmerWithInsurance };
};

const RentChartEmbed: React.FC<RentChartEmbedProps> = ({ initialRentValue, initialCropName }) => {
  const [rentValue, setRentValue] = useState<number | null>(initialRentValue ?? null);
  const [cropName, setCropName] = useState<string>(initialCropName ?? "");
  const [showChart, setShowChart] = useState(!!initialRentValue);
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  // Listen for spinner events
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && typeof detail.rentValue === "number") {
        setRentValue(detail.rentValue);
        setCropName(detail.cropName || "");
        setShowChart(true);
      }
    };

    window.addEventListener("rentcurves:calculated", handler);
    return () => window.removeEventListener("rentcurves:calculated", handler);
  }, []);

  const chartOptions = useMemo<Highcharts.Options>(() => {
    const data = generateChartData(rentValue || 200);

    return {
      chart: {
        type: "line",
        backgroundColor: "transparent",
        style: { fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
        height: 350,
      },
      title: {
        text: "Rent Income & Farmer Income",
        style: { fontWeight: "bold", fontSize: "16px", color: "#1f2937" },
      },
      xAxis: {
        title: { text: "Crop Yield", style: { color: "#6b7280", fontSize: "12px" } },
        categories: yieldPoints.map(String),
        gridLineWidth: 0,
        lineColor: "#e5e7eb",
        tickColor: "#e5e7eb",
        labels: { style: { color: "#6b7280", fontSize: "11px" } },
      },
      yAxis: {
        title: { text: "Rent per Acre", style: { color: "#6b7280", fontSize: "12px" } },
        gridLineColor: "#e5e7eb",
        min: -300,
        max: 450,
        labels: { style: { color: "#6b7280", fontSize: "11px" }, format: "${value}" },
      },
      legend: {
        align: "center",
        verticalAlign: "bottom",
        layout: "horizontal",
        itemStyle: { color: "#374151", fontSize: "12px", fontWeight: "normal" },
        itemHoverStyle: { color: "#111827" },
      },
      plotOptions: {
        line: {
          marker: { enabled: true, radius: 4, symbol: "circle" },
          lineWidth: 2,
        },
      },
      credits: { enabled: false },
      tooltip: {
        valuePrefix: "$",
        valueSuffix: "/acre",
        shared: true,
        crosshairs: true,
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        borderColor: "#e5e7eb",
        borderRadius: 8,
        style: { fontSize: "12px" },
      },
      series: [
        { name: "Landowner Rent - No Insurance", color: "#2D5016", data: data.landownerNoInsurance, type: "line" },
        { name: "Landowner Rent - With Insurance", color: "#1E3A8A", data: data.landownerWithInsurance, type: "line" },
        { name: "Farmer Net Inc - No Insurance", color: "#DC2626", data: data.farmerNoInsurance, type: "line" },
        { name: "Farmer Net Inc - With Insurance", color: "#D97706", data: data.farmerWithInsurance, type: "line" },
      ],
    };
  }, [rentValue]);

  const styles = {
    container: {
      all: "initial" as const,
      display: "block",
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      width: "100%",
      maxWidth: "900px",
      margin: "0 auto",
      boxSizing: "border-box" as const,
      lineHeight: 1.5,
      color: "#374151",
    },
    card: {
      background: "#ffffff",
      borderRadius: "16px",
      border: "1px solid #E5E7EB",
      padding: "24px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      position: "relative" as const,
      overflow: "hidden" as const,
      minHeight: "350px",
    },
    placeholder: {
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center",
      justifyContent: "center",
      minHeight: "350px",
      background: "rgba(249, 250, 251, 0.7)",
      borderRadius: "12px",
    },
    placeholderIcon: {
      width: "48px",
      height: "48px",
      color: "#9CA3AF",
      marginBottom: "16px",
    },
    placeholderText: {
      fontSize: "18px",
      fontWeight: 500,
      color: "#6B7280",
      textAlign: "center" as const,
      padding: "0 16px",
    },
    placeholderSubtext: {
      fontSize: "14px",
      color: "#9CA3AF",
      marginTop: "4px",
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {showChart && rentValue ? (
          <HighchartsReact ref={chartRef} highcharts={Highcharts} options={chartOptions} />
        ) : (
          <div style={styles.placeholder}>
            {/* Wheat icon SVG inline */}
            <svg
              style={styles.placeholderIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M2 22 16 8" />
              <path d="M3.47 12.53 5 11l1.53 1.53a3.5 3.5 0 0 1 0 4.94L5 19l-1.53-1.53a3.5 3.5 0 0 1 0-4.94Z" />
              <path d="M7.47 8.53 9 7l1.53 1.53a3.5 3.5 0 0 1 0 4.94L9 15l-1.53-1.53a3.5 3.5 0 0 1 0-4.94Z" />
              <path d="M11.47 4.53 13 3l1.53 1.53a3.5 3.5 0 0 1 0 4.94L13 11l-1.53-1.53a3.5 3.5 0 0 1 0-4.94Z" />
              <path d="M20 2h2v2a4 4 0 0 1-4 4h-2V6a4 4 0 0 1 4-4Z" />
              <path d="M11.47 17.47 13 19l-1.53 1.53a3.5 3.5 0 0 1-4.94 0L5 19l1.53-1.53a3.5 3.5 0 0 1 4.94 0Z" />
              <path d="M15.47 13.47 17 15l-1.53 1.53a3.5 3.5 0 0 1-4.94 0L9 15l1.53-1.53a3.5 3.5 0 0 1 4.94 0Z" />
              <path d="M19.47 9.47 21 11l-1.53 1.53a3.5 3.5 0 0 1-4.94 0L13 11l1.53-1.53a3.5 3.5 0 0 1 4.94 0Z" />
            </svg>
            <p style={styles.placeholderText}>
              Select a crop and hit "Calculate Rent"
            </p>
            <p style={styles.placeholderSubtext}>
              to see rent and income projections
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RentChartEmbed;
